import React, { useState } from 'react';
import {
  Card,
  Typography,
  Divider,
  Tag,
  Space,
  Tabs,
  Alert,
  message
} from 'antd';
import {
  ApiOutlined,
  CodeOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  CopyOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const UserAPIDocumentation = () => {
  const [activeTab, setActiveTab] = useState('1');

  // 复制代码到剪贴板
  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      message.success('已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败，请手动复制');
    });
  };

  // 代码示例组件
  const CodeBlock = ({ code, language = 'bash' }) => (
    <div style={{ position: 'relative' }}>
      <pre
        style={{
          background: '#f5f5f5',
          padding: '16px',
          borderRadius: '4px',
          overflow: 'auto',
          fontSize: '13px',
          fontFamily: 'Monaco, Consolas, monospace',
          position: 'relative'
        }}
      >
        <code>{code}</code>
      </pre>
      <CopyOutlined
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          cursor: 'pointer',
          fontSize: '16px',
          color: '#666'
        }}
        onClick={() => copyCode(code)}
      />
    </div>
  );

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Card
        title={
          <Space>
            <FileTextOutlined style={{ fontSize: 20 }} />
            <span>API 接口文档</span>
          </Space>
        }
      >
        {/* 基本信息 */}
        <Alert
          message="公共API接口"
          description={
            <div>
              <p><strong>接口地址：</strong>https://ddos.data.setonink.com</p>
              <p style={{ marginBottom: 0 }}><strong>Base URL：</strong>/api/public</p>
            </div>
          }
          type="info"
          showIcon
          icon={<ApiOutlined />}
          style={{ marginBottom: 24 }}
        />

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* 1. AI对话接口 */}
          <TabPane
            tab={
              <span>
                <CodeOutlined />
                AI对话接口
              </span>
            }
            key="1"
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Title level={4}>POST /api/public/ai/chat</Title>
                <Text type="secondary">发送消息给AI助手并获取回复</Text>
              </div>

              <Divider />

              {/* 认证方式 */}
              <div>
                <Title level={5}>
                  <SafetyOutlined /> 认证方式
                </Title>
                <Text>支持以下三种认证方式（任选其一）：</Text>
                
                <div style={{ marginTop: 16 }}>
                  <Tag color="blue">方式1: Authorization header</Tag>
                  <CodeBlock
                    code={`curl -X POST https://ddos.data.setonink.com/api/public/ai/chat \\
  -H "Authorization: Bearer sk-xxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "查询IP黑洞状态"}'`}
                  />
                </div>

                <div style={{ marginTop: 16 }}>
                  <Tag color="green">方式2: X-API-Key header</Tag>
                  <CodeBlock
                    code={`curl -X POST https://ddos.data.setonink.com/api/public/ai/chat \\
  -H "X-API-Key: sk-xxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "查询IP黑洞状态"}'`}
                  />
                </div>

                <div style={{ marginTop: 16 }}>
                  <Tag color="orange">方式3: Query参数</Tag>
                  <CodeBlock
                    code={`curl -X POST "https://ddos.data.setonink.com/api/public/ai/chat?apiKey=sk-xxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "查询IP黑洞状态"}'`}
                  />
                </div>
              </div>

              <Divider />

              {/* 请求体 */}
              <div>
                <Title level={5}>请求体</Title>
                <CodeBlock
                  language="json"
                  code={`{
  "message": "用户消息内容",
  "sessionId": "可选的会话ID"
}`}
                />
              </div>

              <Divider />

              {/* 响应示例 */}
              <div>
                <Title level={5}>响应示例</Title>
                <Tag color="success">成功响应</Tag>
                <CodeBlock
                  language="json"
                  code={`{
  "success": true,
  "data": {
    "sessionId": "674b8e7f1234567890abcdef",
    "userMessage": {
      "_id": "674b8e7f1234567890abcd01",
      "role": "user",
      "content": "查询 192.168.0.1 的攻击情况",
      "createdAt": "2025-12-25T10:30:00.000Z"
    },
    "assistantMessage": {
      "_id": "674b8e7f1234567890abcd02",
      "role": "assistant",
      "content": "正在为您查询 IP 192.168.0.1 的攻击情况...\\n\\n### 攻击统计\\n...",
      "createdAt": "2025-12-25T10:30:02.000Z"
    }
  }
}`}
                />

                <div style={{ marginTop: 16 }}>
                  <Tag color="error">错误响应</Tag>
                  <CodeBlock
                    language="json"
                    code={`{
  "success": false,
  "error": "Rate limit exceeded",
  "message": "QPS limit of 10 requests per second exceeded",
  "retryAfter": 1
}`}
                  />
                </div>
              </div>
            </Space>
          </TabPane>

          {/* 2. 获取会话历史 */}
          <TabPane
            tab={
              <span>
                <FileTextOutlined />
                获取会话历史
              </span>
            }
            key="2"
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Title level={4}>GET /api/public/ai/session/:sessionId</Title>
                <Text type="secondary">获取指定会话的历史消息记录</Text>
              </div>

              <Divider />

              {/* 请求示例 */}
              <div>
                <Title level={5}>请求示例</Title>
                <CodeBlock
                  code={`curl -X GET "https://ddos.data.setonink.com/api/public/ai/session/session_id" \\
  -H "X-API-Key: sk-xxxxxxxxxxxxx"`}
                />
              </div>

              <Divider />

              {/* 响应示例 */}
              <div>
                <Title level={5}>响应示例</Title>
                <CodeBlock
                  language="json"
                  code={`{
  "success": true,
  "data": {
    "session": {
      "id": "session_id",
      "title": "对话标题",
      "messageCount": 10,
      "createdAt": "2025-12-22T10:00:00.000Z"
    },
    "messages": [
      {
        "role": "user",
        "content": "用户消息",
        "createdAt": "2025-12-22T10:00:00.000Z"
      },
      {
        "role": "assistant",
        "content": "AI回复",
        "createdAt": "2025-12-22T10:00:01.000Z"
      }
    ]
  }
}`}
                />
              </div>
            </Space>
          </TabPane>

          {/* 3. 获取API密钥统计 */}
          <TabPane
            tab={
              <span>
                <ThunderboltOutlined />
                获取API密钥统计
              </span>
            }
            key="3"
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Title level={4}>GET /api/public/api-key/stats</Title>
                <Text type="secondary">获取当前API密钥的使用统计信息</Text>
              </div>

              <Divider />

              {/* 请求示例 */}
              <div>
                <Title level={5}>请求示例</Title>
                <CodeBlock
                  code={`curl -X GET "https://ddos.data.setonink.com/api/public/api-key/stats" \\
  -H "X-API-Key: sk-xxxxxxxxxxxxx"`}
                />
              </div>

              <Divider />

              {/* 响应示例 */}
              <div>
                <Title level={5}>响应示例</Title>
                <CodeBlock
                  language="json"
                  code={`{
  "success": true,
  "data": {
    "qpsLimit": 10,
    "dailyLimit": 10000,
    "todayUsage": 156,
    "totalUsage": 1234,
    "sessionLimit": 1000,
    "sessionCount": 45,
    "lastUsedAt": "2025-12-25T10:35:00.000Z",
    "remainingToday": 9844,
    "remainingSessions": 955
  }
}`}
                />
              </div>

              <Divider />

              {/* 字段说明 */}
              <div>
                <Title level={5}>字段说明</Title>
                <Card size="small">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text strong>qpsLimit:</Text> <Text type="secondary">每秒请求限制（QPS）</Text>
                    </div>
                    <div>
                      <Text strong>dailyLimit:</Text> <Text type="secondary">每日请求限制</Text>
                    </div>
                    <div>
                      <Text strong>todayUsage:</Text> <Text type="secondary">今日已使用次数</Text>
                    </div>
                    <div>
                      <Text strong>totalUsage:</Text> <Text type="secondary">总使用次数</Text>
                    </div>
                    <div>
                      <Text strong>sessionLimit:</Text> <Text type="secondary">会话数量限制（0=不限制）</Text>
                    </div>
                    <div>
                      <Text strong>sessionCount:</Text> <Text type="secondary">已创建会话总数</Text>
                    </div>
                    <div>
                      <Text strong>lastUsedAt:</Text> <Text type="secondary">最后使用时间</Text>
                    </div>
                    <div>
                      <Text strong>remainingToday:</Text> <Text type="secondary">今日剩余请求次数</Text>
                    </div>
                    <div>
                      <Text strong>remainingSessions:</Text> <Text type="secondary">剩余可创建会话数（-1=无限制）</Text>
                    </div>
                  </Space>
                </Card>
              </div>
            </Space>
          </TabPane>

          {/* 通用说明 */}
          <TabPane
            tab={
              <span>
                <SafetyOutlined />
                使用须知
              </span>
            }
            key="4"
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Alert
                message="重要提示"
                description="请妥善保管您的API密钥，不要在公开场合或客户端代码中暴露密钥。"
                type="warning"
                showIcon
              />

              <div>
                <Title level={5}>请求限制</Title>
                <Card size="small">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text strong>QPS限制：</Text> <Text>默认为10次/秒，超过限制将返回429错误</Text>
                    </div>
                    <div>
                      <Text strong>每日限制：</Text> <Text>默认为10000次/天，超过限制将返回401错误</Text>
                    </div>
                    <div>
                      <Text strong>会话限制：</Text> <Text>默认为1000个，超过限制将返回401错误</Text>
                    </div>
                    <div>
                      <Text strong>超时时间：</Text> <Text>所有接口请求超时时间为30秒</Text>
                    </div>
                    <div>
                      <Text strong>限制重置：</Text> <Text>每日限制在每天 00:00 (UTC+8) 重置</Text>
                    </div>
                  </Space>
                </Card>
              </div>

              <div>
                <Title level={5}>错误码说明</Title>
                <Card size="small">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Tag color="error">400</Tag> 
                      <Text strong> Bad Request</Text>
                      <Text type="secondary" style={{ display: 'block', marginLeft: 50 }}>
                        请求参数错误 - 请检查请求体格式和参数
                      </Text>
                    </div>
                    <div>
                      <Tag color="error">401</Tag> 
                      <Text strong> Unauthorized</Text>
                      <Text type="secondary" style={{ display: 'block', marginLeft: 50 }}>
                        API密钥无效、已禁用、未提供，或超过每日/会话限制 - 请检查API密钥和使用情况
                      </Text>
                    </div>
                    <div>
                      <Tag color="error">404</Tag> 
                      <Text strong> Not Found</Text>
                      <Text type="secondary" style={{ display: 'block', marginLeft: 50 }}>
                        会话不存在或无权访问 - 请检查会话ID是否正确
                      </Text>
                    </div>
                    <div>
                      <Tag color="error">429</Tag> 
                      <Text strong> Rate Limit Exceeded</Text>
                      <Text type="secondary" style={{ display: 'block', marginLeft: 50 }}>
                        超过QPS或每日限制 - 请降低请求频率或等待限制重置
                      </Text>
                    </div>
                    <div>
                      <Tag color="error">500</Tag> 
                      <Text strong> Internal Server Error</Text>
                      <Text type="secondary" style={{ display: 'block', marginLeft: 50 }}>
                        服务器内部错误 - 请稍后重试
                      </Text>
                    </div>
                    <div>
                      <Tag color="error">503</Tag> 
                      <Text strong> Service Unavailable</Text>
                      <Text type="secondary" style={{ display: 'block', marginLeft: 50 }}>
                        AI助手维护中（非白名单用户）- 请等待维护完成
                      </Text>
                    </div>
                  </Space>
                </Card>
              </div>

              <div>
                <Title level={5}>最佳实践</Title>
                <Card size="small">
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    <li>实现指数退避重试机制处理429错误</li>
                    <li>使用连接池复用HTTP连接</li>
                    <li>在服务端而非客户端调用API</li>
                    <li>定期轮换API密钥以提高安全性</li>
                    <li>监控API使用情况，避免超过限制</li>
                    <li>记录请求日志以便问题排查</li>
                  </ul>
                </Card>
              </div>
            </Space>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default UserAPIDocumentation;

